// Admin/Invoices/ViewInvoice/ViewInvoice.jsx  (or ViewInvoicePage.jsx)
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Classes from "./ViewInvoicePage.module.css";
import MoreDropDown from "./MoreDropDown";
import Logo from "../../../../LocalComp/Logo/Logo";
import DropDown from "../StatusDropDown/DropDown";
import { AnimatePresence } from "framer-motion";
import EmailInvoicePopup from "../RecordTable/EmailInvoicePopup/EmailInvoicePopup";
import backend from '../../../../../../api';

const LeftArrow = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 448 512">
    <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
  </svg>
);

function fmtDate(d) {
  if (!d) return '-';
  const x = new Date(d);
  return x.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function money(n, c = 'USD') {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: 'currency', currency: (c || 'USD').toUpperCase() });
}

// ✅ Subscription active helper (same logic pattern you use elsewhere)
function isSubscriptionActive(as) {
  if (!as) return false;
  const status = String(as.status || 'none').toLowerCase();
  if (status === 'none' || status === 'canceled' || status === 'cancelled') return false;

  const now = Date.now();
  const startOk = as.current_period_start ? new Date(as.current_period_start).getTime() <= now : true;
  const endOk   = as.current_period_end   ? new Date(as.current_period_end).getTime()   >= now : false;
  return startOk && endOk;
}

// ✅ Find delivery fee line from invoice lines (very tolerant)
function findDeliveryLine(lines = []) {
  const arr = Array.isArray(lines) ? lines : [];
  const keywords = ['delivery fee', 'delivery', 'fill up fee', 'fill-up fee', 'service fee'];
  const match = arr.find((li) => {
    const d = String(li?.description || '').toLowerCase();
    return keywords.some((k) => d.includes(k));
  });
  return match || null;
}

export default function ViewInvoice() {
  const { id } = useParams();
  const navigateTo = useNavigate();

  const [openEmailInvoicePopup, setopenEmailInvoicePopup] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);

  // the area we will render into a PDF
  const pdfRef = useRef(null);

  const handleCloseEmailPopup = () => setopenEmailInvoicePopup(false);
  const handleOpenEmailPopup = () => setopenEmailInvoicePopup(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await backend.get(`/api/invoices/${id}`);
        const inv = data?.invoice || data?.data || data;
        if (alive) setInvoice(inv || null);
      } catch (e) {
        console.error('Fetch invoice error:', e);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const currency = (invoice?.currency || 'USD').toUpperCase();
  const refundedTotal = Math.max(0, Number(invoice?.refundedTotal || 0));
  const total = Math.max(0, Number(invoice?.total || 0));
  const netTotal = Math.max(0, total - refundedTotal);

  // Build line rows for table
  const rows = useMemo(() => {
    if (!invoice?.lines) return [];
    return invoice.lines.map(li => ({
      desc: li.description,
      unitPrice: money(li.unitPrice, currency),
      qty: li.quantity,
      amount: money(li.amount, currency),
    }));
  }, [invoice, currency]);

  // 🔹 Display name (supports registered & guest / cash invoices)
  const displayName = useMemo(() => {
    if (!invoice) return '-';

    const u = invoice.userId;
    const fromUser =
      u?.name ||
      [u?.firstName, u?.lastName].filter(Boolean).join(' ') ||
      u?.email ||
      u?.phone;

    const fromSnapshot =
      invoice.guestName ||
      invoice.guestEmail ||
      invoice.guestPhone;

    return fromUser || fromSnapshot || 'Customer';
  }, [invoice]);

  // 🔹 Contact line (email + phone, from user or snapshot)
  const displayContact = useMemo(() => {
    if (!invoice) return '';

    const email = invoice.userId?.email || invoice.guestEmail || '';
    const phone = invoice.userId?.phone || invoice.guestPhone || '';

    if (email && phone) return `${email} • ${phone}`;
    return email || phone || '';
  }, [invoice]);

  // 🔹 Payment method: supports cash / online / legacy Stripe
  const paymentMethod = useMemo(() => {
    if (!invoice) return '-';
    if (invoice.invoiceType === 'cash') return 'Cash';
    if (invoice.invoiceType === 'online') return 'Online payment';
    // fallback for older invoices
    return 'Stripe';
  }, [invoice]);

  // 🔹 Whether this invoice can be paid online
  const canPay = useMemo(() => {
    if (!invoice) return false;
    // cash invoices are already paid manually
    if (invoice.invoiceType === 'cash') return false;
    return (invoice.status || '').toLowerCase() !== 'completed';
  }, [invoice]);

  // ✅ Membership savings line (frontend-only, tolerant)
  const membershipSavings = useMemo(() => {
    if (!invoice) return { show: false, saved: 0 };

    // 1) try any explicit flags you might already have
    const flag =
      invoice?.membershipApplied ||
      invoice?.meta?.membershipApplied ||
      invoice?.meta?.subscriptionApplied ||
      invoice?.meta?.deliveryFeeWaived;

    // 2) try to infer subscription snapshot from invoice payload (if populated)
    const snapSub =
      invoice?.subscriptionSnapshot ||
      invoice?.meta?.subscriptionSnapshot ||
      invoice?.userId?.activeSubscription ||
      invoice?.userSnapshot?.activeSubscription;

    const subActive = !!flag || isSubscriptionActive(snapSub);

    // 3) infer delivery fee + waiver
    const deliveryLine = findDeliveryLine(invoice?.lines);
    const deliveryAmount = Number(deliveryLine?.amount ?? deliveryLine?.unitPrice ?? NaN);

    // delivery fee “list price”: try known fields, else fallback 9.99
    const listPrice =
      Number(invoice?.meta?.deliveryFeeListPrice) ||
      Number(invoice?.deliveryFeeListPrice) ||
      Number(invoice?.serviceFees?.delivery) ||
      Number(invoice?.fees?.delivery) ||
      9.99;

    // if we see a delivery line with 0 amount and subscription active => saved listPrice
    const deliveryWasWaived = Number.isFinite(deliveryAmount) ? deliveryAmount === 0 : false;

    // If invoice has explicit fields, prefer them
    const explicitSaved =
      Number(invoice?.meta?.savedDeliveryFee) ||
      Number(invoice?.savedDeliveryFee) ||
      0;

    const saved = explicitSaved > 0
      ? explicitSaved
      : (subActive && deliveryWasWaived ? listPrice : 0);

    return { show: saved > 0, saved };
  }, [invoice]);

  // ───────────────── Download to PDF ─────────────────
  const handleDownload = async () => {
    if (!pdfRef.current) return;
    try {
      setDownloading(true);

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const HTML2CANVAS_SCALE = 1.25;
      const MAX_EXPORT_WIDTH = 1400;
      const JPEG_QUALITY = 0.72;
      const MARGIN_MM = 0;

      const canvasFull = await html2canvas(pdfRef.current, {
        scale: HTML2CANVAS_SCALE,
        backgroundColor: '#ffffff',
        useCORS: true,
        windowWidth: document.documentElement.scrollWidth,
      });

      let exportCanvas = canvasFull;
      if (canvasFull.width > MAX_EXPORT_WIDTH) {
        const scaled = document.createElement('canvas');
        const ratio = MAX_EXPORT_WIDTH / canvasFull.width;
        scaled.width = MAX_EXPORT_WIDTH;
        scaled.height = Math.round(canvasFull.height * ratio);
        const sctx = scaled.getContext('2d');
        sctx.imageSmoothingEnabled = true;
        sctx.imageSmoothingQuality = 'high';
        sctx.drawImage(canvasFull, 0, 0, scaled.width, scaled.height);
        exportCanvas = scaled;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - 2 * MARGIN_MM;
      const usableH = pageH - 2 * MARGIN_MM;

      const mmPerPx = usableW / exportCanvas.width;
      const pagePixels = Math.floor(usableH / mmPerPx);

      let y = 0;
      let pageIndex = 0;
      while (y < exportCanvas.height) {
        const sliceHeight = Math.min(pagePixels, exportCanvas.height - y);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = exportCanvas.width;
        sliceCanvas.height = sliceHeight;

        const sctx = sliceCanvas.getContext('2d');
        sctx.drawImage(
          exportCanvas,
          0, y, exportCanvas.width, sliceHeight,
          0, 0, sliceCanvas.width, sliceCanvas.height
        );

        const img = sliceCanvas.toDataURL('image/jpeg', JPEG_QUALITY);

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(img, 'JPEG', MARGIN_MM, MARGIN_MM, usableW, sliceHeight * mmPerPx, undefined, 'FAST');

        y += sliceHeight;
        pageIndex += 1;
      }

      const filename = `Invoice-${invoice?.invoiceNumber || id}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error('PDF download failed:', err);
      try { window.print(); } catch {}
    } finally {
      setDownloading(false);
    }
  };

  // ───────────────── Pay Invoice (only for online, not completed) ─────────────────
  const handlePay = async () => {
    try {
      setPaying(true);
      const { data } = await backend.post(`/api/invoices/${id}/pay`);
      if (data?.ok && data.url) {
        window.location.assign(data.url);
      } else {
        alert(data?.error || 'Could not start payment');
      }
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setPaying(false);
    }
  };

  const RefundedPill = refundedTotal > 0 ? (
    <span
      style={{
        marginLeft: 8,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(99,102,241,0.12)',
        border: '1px solid rgba(99,102,241,0.35)',
        color: '#6366F1',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
      title="Refund applied"
    >
      {`Refunded ${money(refundedTotal, currency)}`}
    </span>
  ) : null;

  return (
    <>
      <div className={Classes.container}>
        <div className={Classes.header}>
          <button onClick={() => navigateTo(-1)} className={Classes.backButton}>
            <LeftArrow height='12px' fill='#969696' /><h5>Go back</h5>
          </button>
          <div className="flexAlign10">
            <button className={Classes.addBtn} onClick={handleDownload} disabled={downloading}>
              <svg xmlns="http://www.w3.org/2000/svg" height='12px' fill="white" viewBox="0 0 512 512">
                <path d="M256 0a256 256 0 1 0 0 512A256 256 0 1 0 256 0zM244.7 395.3l-112-112c-4.6-4.6-5.9-11.5-3.5-17.4s8.3-9.9 14.8-9.9l64 0 0-96c0-17.7 14.3-32 32-32l32 0c17.7 0 32 14.3 32 32l0 96 64 0c6.5 0 12.3 3.9 14.8 9.9s1.1 12.9-3.5 17.4l-112 112c-6.2 6.2-16.4 6.2-22.6 0z" />
              </svg>
              <h5>{downloading ? 'Downloading…' : 'Download'}</h5>
            </button>
            <MoreDropDown
              canPay={canPay}
              onPay={handlePay}
              onDownload={handleDownload}
              handleOpenEmailPopup={handleOpenEmailPopup}
              paying={paying}
            />
          </div>
        </div>

        {/* Everything inside this wrapper ends up in the PDF */}
        <div ref={pdfRef} className={Classes.hero} style={{ background: '#fff' }}>
          <div className={Classes.grid}>
            <div className={Classes.heroLeft}>
              <div className={Classes.logo}><Logo alwaysShowText={true} /></div>
              <h5 className={`${Classes.lightText} mt-10`}>Fuel Clubs</h5>
              <div className={Classes.invoicedBy}>
                <h5>Invoiced to</h5>
                <p className={Classes.lightText}>{displayName}</p>
                {displayContact && <p className={Classes.lightText}>{displayContact}</p>}
              </div>
            </div>

            <div className={Classes.heroRight}>
              <div className={Classes.invoice}>
                <div className="flexEnd10" style={{ alignItems: 'center' }}>
                  <h3>Invoice</h3>
                  <DropDown
                    selectedValue={(invoice?.status || 'pending').replace(/\b\w/g, m => m.toUpperCase())}
                    isDropDown={false}
                  />
                  {RefundedPill}
                </div>
                <div className={Classes.invoiceListCon}>
                  <div>
                    <h5 className={Classes.lightText}>Invoice number</h5>
                    <h5 className={Classes.lightText}>{invoice?.invoiceNumber || '-'}</h5>
                  </div>
                  <div>
                    <h5 className={Classes.lightText}>Unique ID</h5>
                    <h5 className={Classes.lightText}>{invoice?._id || '-'}</h5>
                  </div>
                  <div>
                    <h5 className={Classes.lightText}>Date issued</h5>
                    <h5 className={Classes.lightText}>{fmtDate(invoice?.issuedAt)}</h5>
                  </div>
                  <div>
                    <h5 className={Classes.lightText}>Date paid</h5>
                    <h5 className={Classes.lightText}>{invoice?.paidAt ? fmtDate(invoice.paidAt) : '-'}</h5>
                  </div>
                  <div>
                    <h5 className={Classes.lightText}>Payment method</h5>
                    <h5 className={Classes.lightText}>{paymentMethod}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={Classes.hero2}>
            <table className={Classes.table}>
              <thead>
                <tr>
                  <th><h5 className={Classes.lightDarkText}>Item</h5></th>
                  <th><h5 className={Classes.lightDarkText}>Price Per Gallon / Fee</h5></th>
                  <th><h5 className={Classes.lightDarkText}>Qty</h5></th>
                  <th><h5 className={Classes.lightDarkText}>Total</h5></th>
                </tr>
              </thead>

              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td><h5 className={Classes.itemLink}>{r.desc}</h5></td>
                    <td><h5>{r.unitPrice}</h5></td>
                    <td><h5>x{r.qty}</h5></td>
                    <td><h5>{r.amount}</h5></td>
                  </tr>
                ))}

                {/* Subtotal */}
                <tr>
                  <td></td><td></td>
                  <td><h5 className={Classes.fw600}>Subtotal</h5></td>
                  <td><h5>{money(invoice?.subtotal, currency)}</h5></td>
                </tr>

                {/* ✅ Membership savings line (requested) */}
                {membershipSavings?.show && (
                  <tr>
                    <td></td><td></td>
                    <td>
                      <h5 className={Classes.fw600}>
                        Savings (Membership)
                      </h5>
                    </td>
                    <td>
                      <h5 style={{ color: '#16a34a', fontWeight: 700 }}>
                        {money(membershipSavings.saved, currency)} saved due to subscription
                      </h5>
                    </td>
                  </tr>
                )}

                {/* Refunded row (if any) */}
                {refundedTotal > 0 && (
                  <tr>
                    <td></td><td></td>
                    <td><h5>Refunded</h5></td>
                    <td><h5>-{money(refundedTotal, currency)}</h5></td>
                  </tr>
                )}

                {/* Final total after refunds */}
                <tr>
                  <td></td><td></td>
                  <td><h5>{refundedTotal > 0 ? 'Total after refunds' : 'Total'}</h5></td>
                  <td><h5>{money(netTotal, currency)}</h5></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {openEmailInvoicePopup && (
          <EmailInvoicePopup
            handleClose={handleCloseEmailPopup}
            invoiceId={invoice?._id}
          />
        )}
      </AnimatePresence>
    </>
  );
}
